import React, {
	useState,
	useRef,
	useImperativeHandle,
	forwardRef,
	useMemo,
} from 'react';
  
const Carousel = forwardRef(({
	items,
	visibleSlides = 1.3,
	hideArrows = false,
	gap = 20,
}, ref) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	
	const totalItems = items.length;
	const itemWidthPercent = 100 / visibleSlides;

	const maxIndex = useMemo(() => {
		return Math.max(0, Math.floor(totalItems - visibleSlides));
	}, [totalItems, visibleSlides]);
	
	const getTranslateX = (index) => {
		// If we're at the last valid index AND visibleSlides is fractional
		const isLastSlide = index === maxIndex;
		const isFractional = visibleSlides % 1 !== 0;
	
		if (isLastSlide && isFractional) {
		  const fullVisible = Math.floor(visibleSlides);
		  const fraction = visibleSlides - fullVisible;
		  const remainingItems = totalItems - fullVisible;
		  const isPartial = remainingItems > fullVisible;
	
		  if (isPartial) {
			// Offset so that the last visible item aligns right edge
			const hiddenPartPercent = fraction * itemWidthPercent;
			const translatePercent = index * itemWidthPercent - hiddenPartPercent;
			const translatePx = index * gap;
			return `translateX(calc(-${translatePercent}% - ${translatePx}px))`;
		  }
		}
	
		// Default transform
		return `translateX(calc(-${index * itemWidthPercent}% - ${
		  index * gap
		}px))`
	};
  
	const goToIndex = (index) => {
	  	setCurrentIndex(Math.max(0, Math.min(maxIndex, index)));
	}
  
	const nextSlide = () => {
	  	if (currentIndex < maxIndex) goToIndex(currentIndex + 1);
	}
  
	const prevSlide = () => {
	  	if (currentIndex > 0) goToIndex(currentIndex - 1);
	}
  
	useImperativeHandle(ref, () => ({
		next: nextSlide,
		prev: prevSlide,
		goTo: goToIndex,
		index: () => currentIndex,
	}))
  
	return (
		<div className="carousel-wrapper">
			{!hideArrows && (
				<button
					className={`arrow prev ${currentIndex === 0 ? 'hidden-disabled' : ''}`}
					onClick={prevSlide}
					disabled={currentIndex === 0}
				>
					&#10094;
				</button>
			)}
	
			<div className="carousel-track-wrapper">
				<div
					className="carousel-track"
					style={{
						transform: getTranslateX(currentIndex),
						gap: `${gap}px`,
					}}
				>
					{items.map((item, index) => (
						<div
							className="carousel-item"
							key={index}
							style={{ flex: `0 0 ${itemWidthPercent}%` }}
						>
							{item}
						</div>
					))}
				</div>
			</div>
					
			{!hideArrows && (
				<button
					className={`arrow next ${currentIndex === maxIndex ? 'hidden-disabled' : ''}`}
					onClick={nextSlide}
					disabled={currentIndex === maxIndex}
				>
					&#10095;
				</button>
			)}
		</div>
	)
})
  
export default Carousel;
  